import { Component } from '@theme/component';

/**
 * SANSEVIERIA - Smooth fly-to-bag animation.
 *
 * This is a drop-in replacement for assets/fly-to-cart.js.
 * Uses the browser's compositor-managed Web Animations API instead of
 * reading layout and changing styles on every animation frame.
 *
 * It also accounts for the custom floating header sliding into view.
 * No cart-drawer, discount, checkout, or Razorpay code is modified.
 */
class FlyToCart extends Component {
  /** @type {Element | null} */
  source = null;
  /** @type {Element | null} */
  destination = null;
  /** @type {boolean | string} */
  useSourceSize = false;

  /** @type {number} */
  #frameId = 0;
  /** @type {Animation | null} */
  #flight = null;
  /** @type {number} */
  #fallbackTimer = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#frameId = requestAnimationFrame(() => {
      this.#frameId = 0;
      this.#start();
    });
  }

  disconnectedCallback() {
    if (this.#frameId) cancelAnimationFrame(this.#frameId);
    if (this.#fallbackTimer) clearTimeout(this.#fallbackTimer);
    if (this.#flight) {
      try { this.#flight.cancel(); } catch (_) {}
      this.#flight = null;
    }
    super.disconnectedCallback();
  }

  #selectTarget() {
    const provided = this.destination instanceof Element && this.destination.isConnected
      ? [this.destination]
      : [];
    const candidates = provided.concat(
      Array.from(document.querySelectorAll(
        '.sv-header__cart, .header-actions__cart-icon, [data-testid="cart-drawer-trigger"]'
      ))
    );

    for (const element of candidates) {
      if (!(element instanceof Element) || !element.isConnected) continue;
      const header = element.closest('[data-sv-header-root]');
      if (header && header.classList.contains('sv-header--receded')) continue;
      const style = getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden') continue;
      const elementRect = element.getBoundingClientRect();
      if (elementRect.width < 8 || elementRect.height < 8) continue;

      const icon = element.querySelector('svg');
      const iconRect = icon ? icon.getBoundingClientRect() : null;
      const rect = iconRect && iconRect.width >= 8 && iconRect.height >= 8
        ? iconRect
        : elementRect;
      this.destination = element;
      return { element, icon, header, rect };
    }
    return null;
  }

  #headerShift(header) {
    if (!header || header.classList.contains('sv-header--receded')) {
      return { x: 0, y: 0 };
    }
    // When the floating header is being revealed, getBoundingClientRect()
    // reports its CURRENT moving position. Subtract the remaining CSS
    // translate offset to estimate its final, visible cart-icon position.
    const transform = getComputedStyle(header).transform;
    if (!transform || transform === 'none') return { x: 0, y: 0 };
    const matrix = transform.match(/^matrix(3d)?\(([^)]+)\)$/);
    if (!matrix) return { x: 0, y: 0 };
    const numbers = matrix[2].split(',').map((value) => Number(value.trim()));
    const x = matrix[1] ? numbers[12] : numbers[4];
    const y = matrix[1] ? numbers[13] : numbers[5];
    return {
      x: Number.isFinite(x) ? x : 0,
      y: Number.isFinite(y) ? y : 0
    };
  }

  #point(target, compensateForHeader = false) {
    const rect = target.rect;
    let x = rect.left + rect.width / 2;
    let y = rect.top + rect.height / 2;
    if (compensateForHeader) {
      const shift = this.#headerShift(target.header);
      x -= shift.x;
      y -= shift.y;
    }
    return { x, y };
  }

  async #start() {
    if (!this.isConnected || !(this.source instanceof Element) || !this.source.isConnected) {
      this.remove();
      return;
    }

    // Keep the floating header visible. This event is already supported by
    // the SANSEVIERIA header and does not open or change the cart drawer.
    document.dispatchEvent(new CustomEvent('sv:cart-flight-start'));

    const sourceRect = this.source.getBoundingClientRect();
    const target = this.#selectTarget();
    if (!target || sourceRect.width < 1 || sourceRect.height < 1) {
      this.remove();
      return;
    }

    const start = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2
    };
    const end = this.#point(target, true);
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const isMobile = matchMedia('(max-width: 749px)').matches;
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

    const width = isMobile ? 88 : 76;
    const ratio = Math.max(0.7, Math.min(1.15, sourceRect.width / Math.max(1, sourceRect.height)));
    const height = Math.max(76, Math.min(isMobile ? 112 : 98, width / ratio));

    this.style.setProperty('--width', `${width}px`);
    this.style.setProperty('--height', `${height}px`);
    this.style.position = 'fixed';
    this.style.left = '0';
    this.style.top = '0';
    this.style.animation = 'none';
    this.style.setProperty('translate', 'none');
    this.style.transformOrigin = 'center center';
    this.style.zIndex = '2147483646';
    this.style.pointerEvents = 'none';
    this.style.borderRadius = '14px';
    this.style.border = '1px solid rgba(255,255,255,.75)';
    this.style.boxShadow = '0 8px 20px rgba(0,0,0,.18)';
    this.style.willChange = 'transform, opacity';

    const transformAt = (point, scale) =>
      `translate3d(${point.x}px, ${point.y}px, 0) translate(-50%, -50%) scale(${scale})`;
    const startingTransform = transformAt(start, 1);
    const endingTransform = transformAt(end, 0.16);
    this.style.transform = startingTransform;
    this.style.opacity = reducedMotion ? '0' : '1';

    if (reducedMotion) {
      this.remove();
      return;
    }

    // Curved flight, with all frames performed by the browser compositor.
    // No per-frame DOM measurements, getComputedStyle calls, or JS writes.
    const arc = Math.min(65, Math.max(25, Math.abs(dy) * 0.13));
    const bend = Math.sign(dx || 1) * Math.min(24, Math.abs(dx) * 0.08);
    const first = {
      x: start.x + dx * 0.27 + bend,
      y: start.y + dy * 0.16 + (dy < 0 ? -arc : arc) * 0.65
    };
    const second = {
      x: start.x + dx * 0.73 + bend * 0.3,
      y: start.y + dy * 0.67 + (dy < 0 ? -arc : arc) * 0.3
    };
    const duration = isMobile ? 760 : 680;
    const frames = [
      { transform: startingTransform, opacity: 1, offset: 0 },
      { transform: transformAt(first, 0.98), opacity: 1, offset: 0.28 },
      { transform: transformAt(second, 0.56), opacity: 0.98, offset: 0.72 },
      { transform: endingTransform, opacity: 0.08, offset: 1 }
    ];

    try {
      if (typeof this.animate === 'function') {
        this.#flight = this.animate(frames, {
          duration,
          easing: 'cubic-bezier(0.25, 0.55, 0.25, 1)',
          fill: 'forwards'
        });
        await this.#flight.finished;
      } else {
        // CSS-transform fallback for older embedded browsers.
        this.style.transition = `transform ${duration}ms cubic-bezier(0.25, 0.55, 0.25, 1), opacity ${duration}ms ease-out`;
        await new Promise((resolve) => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (!this.isConnected) return resolve();
              this.style.transform = endingTransform;
              this.style.opacity = '0.08';
            });
          });
          this.#fallbackTimer = setTimeout(resolve, duration + 55);
        });
      }
    } catch (_) {
      // A cancelled animation must never block Add to Bag or checkout.
    }

    if (!this.isConnected) return;
    // One final position check AFTER the header transition has completed.
    // This avoids repeated layout reads during the flight.
    const finalTarget = this.#selectTarget() || target;
    const actual = this.#point(finalTarget);
    const error = Math.hypot(actual.x - end.x, actual.y - end.y);
    if (error > 2 && error < 250 && typeof this.animate === 'function') {
      try {
        const correction = this.animate([
          { transform: endingTransform, opacity: 0.08 },
          { transform: transformAt(actual, 0.12), opacity: 0 }
        ], { duration: 80, easing: 'ease-out', fill: 'forwards' });
        await correction.finished;
      } catch (_) {}
    }

    if (this.isConnected) this.#land(finalTarget);
  }

  #land(target) {
    const bounceTarget = target.icon instanceof Element ? target.icon : target.element;
    if (bounceTarget && typeof bounceTarget.animate === 'function') {
      try {
        bounceTarget.animate([
          { transform: 'scale(1)' },
          { transform: 'scale(1.15)' },
          { transform: 'scale(.98)' },
          { transform: 'scale(1)' }
        ], { duration: 260, easing: 'ease-out' });
      } catch (_) {}
    }

    if (
      matchMedia('(max-width: 749px)').matches &&
      document.visibilityState === 'visible' &&
      typeof navigator.vibrate === 'function'
    ) {
      try { navigator.vibrate(12); } catch (_) {}
    }
    this.remove();
  }
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
